//  Utility interfaces and methods for handling currencies


/**  
 * Interface for indicating a quantity of currency
 * currency: Identification and Formating information for currency
 * value: quantity of currency, formatted based on 'currency'
*/
export interface CurrencyAmount 
{
    currency: CurrencyType
    value: FormatCurrencyValue
}

/**
 * Identifying information for a Currency
 * name: identifying name of currency
 * localization: Standard number format to use in formatting
 * (TODO: Fully implement in GACurrencyInput)
 * formatting: Formatting Details of currency
 */
export interface CurrencyType 
{
    name: string,
    localization: Intl.NumberFormat,
    formatting: CurrencyFormattingDetails
}

/**
 * Formatting specifics for currency
 * prefix: string to append to start of currency value (but before negative sign)
 * suffix: string to append to end of currency value
 * groupSeparator: string to include to separate groupings of digits (typically 3) for ease of reading
 * decimalSeparator: string to use to indicate division between fractional and integer components
 * decimalLimit: number of decimal places to use
 */
export interface CurrencyFormattingDetails
{
    prefix?: string,
    suffix?: string,
    groupSeparator?: string,
    decimalSeparator?: string,
    decimalLimit?: number
}

/**
 * Internal interface for formatting Values
 * string: non-formatted string of input value
 * formatted: formatted string of input value
 * float: numerical value of input value
 */
export interface FormatCurrencyValue
{
    string?: string,
    formatted?: string,
    float?: number
}

/** 
 * Format a string or number into a currency format, converting it to FormatCurrencyValue
 * If String, will attempt to strip formatting before conversion
 */
export const formatCurrencyValue = (val: string | number, format: CurrencyFormattingDetails): FormatCurrencyValue => 
{
    if(val === undefined || val === '')
    {
        return({});
    }

    const floatValue = stripCurrencyFormatting(val, format);
    const floatValueSanitized = Number.isNaN(floatValue) ? undefined: floatValue
    const stringValue = floatValueSanitized?.toString();
    const formattedValue = addCurrencyFormatting(floatValueSanitized, format);

    return ({string: stringValue, formatted: formattedValue, float: floatValueSanitized});
}

/**
 * Add Formatting to a value
 * TODO: Add a localization input to allow for non-en-US Formats
 * @param val number to format
 * @returns formatted string
 */
const addCurrencyFormatting = (val: number | undefined,  format: CurrencyFormattingDetails): string | undefined => 
{
    if(val === undefined)
    {
        return undefined;
    }

    return Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
        .format(val)
        .replace('$',format.prefix? format.prefix : '')
        .replace('.', format.decimalSeparator ? format.decimalSeparator : '.')
        .replace(',', format.groupSeparator ? format.groupSeparator : '')
        .concat(format.suffix ? format.suffix : '');

}

/**
 * Removes Formatting from a string or number
 * @param val value to strip formatting of
 * @returns stripped numerical value
 */
export const stripCurrencyFormatting = (val: string | number | undefined,  format: CurrencyFormattingDetails): number | undefined => 
{
    if( val === undefined)
    {
        return undefined;
    }
    if( typeof val === 'number')
    {
        return parseFloat(val.toFixed(format.decimalLimit));
    }

    return parseFloat(parseFloat(val
        .replace(format.prefix ? format.prefix : '','')
        .replace(format.suffix? format.suffix : '','')
        .replace(format.groupSeparator ? format.groupSeparator : '','')
        .replace(format.decimalSeparator? format.decimalSeparator : '.','.')
    ).toFixed(format.decimalLimit));
}